import React from 'react';
import styled, { keyframes } from "styled-components";

const Home = ({ setWorkflow }) => {
  const items = [
    // {
    //   key: "risk",
    //   title: "Risk Assessment",
    //   description:
    //     "Pinpoint where low scores cluster and identify the key factors driving risk.",
    //   Icon: RiskIcon,
    //   color: "#2c6bed",
    // },
    {
      key: "nora",
      title: "Nora AI",
      description:
        "Chat with our AI assistant to get real-time insights from your surveys.",
      Icon: NoraIcon,
      fg: "#2D70E2",
      bg: "#D5E2F9",
    },
    {
      key: "roi",
      title: "ROI Analysis",
      description:
        "Linking your survey results to organizational performance.",
      Icon: RoiIcon,
      fg: "#27CDA7",
      bg: "#D4F5ED",
    },
    {
      key: "thematic",
      title: "Thematic Analysis",
      description:
        "Uncovering themes that shape people & performance.",
      Icon: ThematicIcon,
      fg: "#FEA711",
      bg: "#FFEDCF",
    },
  ];

  return (
    <Container>
      <Header>
        <Title>AI Toolkit</Title>
        <Subtitle>
          From data to decisions in minutes. Choose a workflow to get started.
        </Subtitle>
      </Header>

      <CardsGrid>
        {items.map(({ key, title, description, Icon, fg, bg }, idx) => (
          <Card
            key={key}
            role="button"
            tabIndex={0}
            onClick={() => setWorkflow(key)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWorkflow(key); } }}
            style={{ animationDelay: `${idx * 60}ms` }}
          >
            <CardTop>
              <IconBox bg={bg}>
                <IconWrapper>
                  <Icon color={fg} fill={fg} />
                </IconWrapper>
              </IconBox>
              <CardTitle>{title}</CardTitle>
            </CardTop>
            <CardBody>
              <CardDescription>{description}</CardDescription>
            </CardBody>
            <CardFooter>
              <ExploreIconButton aria-label={`Start ${title}`}>
                <ExploreArrow />
              </ExploreIconButton>
            </CardFooter>
          </Card>
        ))}
      </CardsGrid>
    </Container>
  );
};

export default Home;

// Icons — minimal inline SVGs with square containers
function RiskIcon({ color = '#64748b' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 12l5-5 4 4 6-6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 10v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4" stroke={color} strokeOpacity="0.6" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function LightningIcon({ color = '#64748b' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 2L3 14h7l-1 8 11-12h-7l0-8z" fill={color}/>
    </svg>
  );
}

export function RoiIcon({ color = '#64748b', fill, gradient, gradientId }) { 
  const gradientRef = gradient ? (gradientId || 'roi-icon-gradient') : null;
  const strokeColor = gradient ? `url(#${gradientRef})` : (fill || color);
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {gradient && (
        <defs>
          <linearGradient id={gradientRef} x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={gradient.from} />
            <stop offset="100%" stopColor={gradient.to} />
          </linearGradient>
        </defs>
      )}
      <path
        d="M6 6L8 4M8 4L6 2M8 4H6C3.79086 4 2 5.79086 2 8M18 18L16 20M16 20L18 22M16 20H18C20.2091 20 22 18.2091 22 16M10.189 6.5C10.8551 3.91216 13.2042 2 16 2C19.3137 2 22 4.68629 22 8C22 10.7957 20.0879 13.1449 17.5001 13.811M14 16C14 19.3137 11.3137 22 8 22C4.68629 22 2 19.3137 2 16C2 12.6863 4.68629 10 8 10C11.3137 10 14 12.6863 14 16Z"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function NoraIcon({ color = '#111827', gradient, gradientId }) {
  const gradientRef = gradient ? (gradientId || 'nora-icon-gradient') : null;
  const strokeColor = gradient ? `url(#${gradientRef})` : color;
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {gradient && (
        <defs>
          <linearGradient id={gradientRef} x1="3" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={gradient.from} />
            <stop offset="100%" stopColor={gradient.to} />
          </linearGradient>
        </defs>
      )}
      <path
        d="M8 9.5H12M8 13H15M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 13.1971 3.23374 14.3397 3.65806 15.3845C3.73927 15.5845 3.77988 15.6845 3.798 15.7653C3.81572 15.8443 3.8222 15.9028 3.82221 15.9839C3.82222 16.0667 3.80718 16.1569 3.77711 16.3374L3.18413 19.8952C3.12203 20.2678 3.09098 20.4541 3.14876 20.5888C3.19933 20.7067 3.29328 20.8007 3.41118 20.8512C3.54589 20.909 3.73218 20.878 4.10476 20.8159L7.66265 20.2229C7.84309 20.1928 7.9333 20.1778 8.01613 20.1778C8.09715 20.1778 8.15566 20.1843 8.23472 20.202C8.31554 20.2201 8.41552 20.2607 8.61549 20.3419C9.6603 20.7663 10.8029 21 12 21Z"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


export function ThematicIcon({ color = '#64748b', gradient, gradientId }) { 
  const gradientRef = gradient ? (gradientId || 'thematic-icon-gradient') : null;
  const strokeColor = gradient ? `url(#${gradientRef})` : color;
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {gradient && (
        <defs>
          <linearGradient id={gradientRef} x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={gradient.from} />
            <stop offset="100%" stopColor={gradient.to} />
          </linearGradient>
        </defs>
      )}
      <path
        d="M15.5 3.5V2M19.4393 4.56066L20.5 3.5M20.5103 8.5H22.0103M21.9506 13C21.4489 18.0533 17.1853 22 12 22C6.47715 22 2 17.5228 2 12C2 6.81465 5.94668 2.5511 11 2.04938M12 8H16V12M15.6197 8C13.2653 11.3276 9.38636 13.5 5 13.5C3.9971 13.5 3.02072 13.3864 2.08302 13.1715"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function LayersIcon({ color = '#64748b' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3l9 5-9 5-9-5 9-5z" stroke={color} strokeWidth="1.8"/>
      <path d="M21 12l-9 5-9-5" stroke={color} strokeOpacity="0.6" strokeWidth="1.6"/>
    </svg>
  );
}

function ChangeIcon({ color = '#64748b' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 14c3-6 8-6 11-2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M13 12l4 0 0-4" stroke={color} strokeOpacity="0.6" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

// Subtle entrance animation (cascade)
const cascadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(2px); }
  60% { opacity: 1; transform: translateY(0) scale(1.01); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

// Layout
const Container = styled.div`
  width: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.div`
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0 0 6px 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.2s ease, border-color 0.25s ease;
  animation: ${cascadeIn} 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform, opacity;
  opacity: 0;
  min-height: 190px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(280px 140px at 10% -20%, rgba(255, 182, 141, 0.22), transparent 60%),
      radial-gradient(240px 120px at 85% -20%, rgba(123, 97, 255, 0.18), transparent 60%),
      radial-gradient(260px 120px at 50% -10%, rgba(44, 107, 237, 0.16), transparent 60%);
    opacity: 1;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #d1d5db;
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
  }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(59,130,246,0.25); }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.bg || '#f8fafc'};
  border: 1px solid #eef2f7;
  border-radius: 8px; /* subtle radius (square with soft corners) */
  color: #64748b;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  svg { width: 22px; height: 22px; display: block; }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CardBody = styled.div`
  margin-top: 10px;
  flex: 1;
`;

const CardDescription = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
`;

const CardFooter = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
`;

const ExploreIconButton = styled.button`
  background: #f8fafc;
  border: 1px solid #e6ecff;
  color: #64748b;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.22s ease, background 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-1px);
    background: #ffffff;
    border-color: #dbe4ff;
    box-shadow: 0 6px 16px rgba(0,0,0,0.06);
  }
`;

const ExploreArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M8 16L16 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M9 8h7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
